import {
  OverflowDirection,
  OverflowDisplayTooltip,
} from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { DotsVerticalIcon } from "@heroicons/react/outline";
import { ReactNode } from "react";
import { Action, DropdownMenu } from "../../../DropdownMenu";
import { CardVariant } from "./constants";
import { LoadingState } from "./types";

interface CardHeaderProps<DataT> {
  icon?: ReactNode;
  title?: string;
  subtitle?: string;
  actions?: Action<DataT>[];
  dropdownMenuItems: Action<DataT>[];
  variant?: CardVariant;
  item?: DataT;
  loadingState?: LoadingState;
}

const getClipPath = (loadingState: LoadingState) => {
  const progress = loadingState.progress * 3.6;
  const centerX = 50;
  const centerY = 50;
  const radius = 100;

  if (progress === 0)
    return "polygon(50% 50%, 50% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%, 50% 0%)";
  if (progress >= 360) return "polygon(50% 50%, 50% 0%, 50% 0%)";

  const remainingProgress = 360 - progress;
  const angle = (-remainingProgress - 90) * (Math.PI / 180);
  const endX = centerX + radius * Math.cos(angle);
  const endY = centerY + radius * Math.sin(angle);

  let polygon = `polygon(${centerX}% ${centerY}%, ${centerX}% 0%`;

  if (remainingProgress > 45) polygon += `, 0% 0%`;
  if (remainingProgress > 135) polygon += `, 0% 100%`;
  if (remainingProgress > 225) polygon += `, 100% 100%`;
  if (remainingProgress > 315) polygon += `, 100% 0%`;

  polygon += `, ${endX}% ${endY}%)`;

  return polygon;
};

export const CardHeader = <DataT,>({
  icon,
  title,
  subtitle,
  actions,
  variant,
  dropdownMenuItems,
  item,
  loadingState,
}: CardHeaderProps<DataT>) => {
  const hasLoadingState = loadingState?.progress;
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center justify-center gap-x-2">
        {icon && (
          <div
            className={`relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full p-1 transition-colors group-hover:bg-blue-100 group-hover:text-blue-500 [&>svg]:group-hover:text-blue-500 ${
              variant === CardVariant.WARNING
                ? "!bg-amber-100 !text-amber-500 [&>svg]:!text-amber-400"
                : "bg-gray-100 text-gray-500 [&>svg]:text-gray-400"
            } ${hasLoadingState ? "" : ""}`}
          >
            {icon}
            {hasLoadingState ? (
              <div
                className="z-1 absolute inset-0 rounded-full bg-white bg-opacity-75"
                style={{
                  clipPath: getClipPath(loadingState),
                }}
              />
            ) : null}
          </div>
        )}
        {title && (
          <h3
            className="flex flex-col justify-center pr-2 font-medium group-hover:text-blue-500"
            title={title}
          >
            <OverflowDisplayTooltip
              content={title}
              className="break-all"
              overflowDirection={OverflowDirection.VERTICAL}
              lineClamp={2}
            >
              {title}
            </OverflowDisplayTooltip>
            {subtitle && (
              <span className="mt-1 text-sm text-gray-400">{subtitle}</span>
            )}
          </h3>
        )}
      </div>
      {actions && (
        <DropdownMenu<DataT>
          menuItems={dropdownMenuItems}
          item={item}
          icon={<DotsVerticalIcon className="h-5 w-5" />}
        />
      )}
    </div>
  );
};
